import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Spinner from '@/components/ui/spinner';
import { useClient } from '@/hooks/use-client';
import toast from '@/toast';
import { isUnavailableError } from '@/utils/errors';

import { useRefreshSession, reconcileSession, type RefreshSession } from './refresh-session';

import type { AsyncRefreshHeader } from 'pl-api';

const messages = defineMessages({
  moreFound: {
    id: 'status.context.more_replies_found',
    defaultMessage: 'More replies found',
  },
  show: {
    id: 'status.context.show',
    defaultMessage: 'Show',
  },
  loadingInitial: {
    id: 'status.context.loading',
    defaultMessage: 'Loading',
  },
  success: {
    id: 'status.context.loading.success',
    defaultMessage: 'New replies loaded',
  },
  error: {
    id: 'status.context.loading.error',
    defaultMessage: 'Couldn’t load new replies',
  },
  retry: {
    id: 'status.context.retry',
    defaultMessage: 'Retry',
  },
});

/**
 * Simple debounce hook implementation
 */
const useDebouncedCallback = (
  callback: () => void,
  delay: number,
  options?: { leading?: boolean; trailing?: boolean },
) => {
  const timeoutRef = useRef<any>(null);
  const isLeading = options?.leading ?? false;
  const isTrailing = options?.trailing ?? true;

  const debouncedFn = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isLeading && !timeoutRef.current) {
      callback();
    }

    if (isTrailing) {
      timeoutRef.current = setTimeout(() => {
        callback();
        timeoutRef.current = null;
      }, delay);
    }
  }, [callback, delay, isLeading, isTrailing]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
};

type LoadingState = 'idle' | 'more-available' | 'loading' | 'success' | 'error';

/**
 * Age of thread below which we consider it new & fetch
 * replies more frequently
 */
const NEW_THREAD_AGE_THRESHOLD = 30 * 60_000;
/**
 * Interval at which we check for new replies for old threads
 */
const LONG_AUTO_FETCH_REPLIES_INTERVAL = 5 * 60_000;
/**
 * Interval at which we check for new replies for new threads.
 * Also used as a threshold to throttle repeated fetch calls
 */
const SHORT_AUTO_FETCH_REPLIES_INTERVAL = 60_000;
/**
 * Number of refresh_async checks at which an early fetch
 * will be triggered if there are results
 */
const LONG_RUNNING_FETCH_THRESHOLD = 3;

/**
 * Returns whether the thread is new, based on NEW_THREAD_AGE_THRESHOLD
 */
const getIsThreadNew = (statusCreatedAt: string) => {
  const now = new Date();
  const newThreadThreshold = new Date(now.getTime() - NEW_THREAD_AGE_THRESHOLD);

  return new Date(statusCreatedAt) > newThreadThreshold;
};

/**
 * This hook kicks off a background check for the async refresh job
 * and loads any newly found replies once the job has finished,
 * and when LONG_RUNNING_FETCH_THRESHOLD was reached and replies were found
 */
const useCheckForReplies = ({
  statusId,
  session,
  refreshHeader,
  isEnabled,
  retryKey,
  onChangeLoadingState,
  onLoadContext,
  onRefresh,
}: {
  statusId: string;
  session: RefreshSession;
  refreshHeader?: AsyncRefreshHeader;
  isEnabled: boolean;
  retryKey: number;
  onChangeLoadingState: (state: LoadingState) => void;
  onLoadContext: () => Promise<void>;
  onRefresh: () => Promise<void>;
}) => {
  const client = useClient();

  const callbacks = useRef({ onChangeLoadingState, onLoadContext, onRefresh });
  callbacks.current = { onChangeLoadingState, onLoadContext, onRefresh };
  const { reconciledHandles } = session;

  const reconciliation = session.reconciliation;
  useEffect(() => {
    if (reconciliation) {
      callbacks.current.onChangeLoadingState(
        reconciliation.state === 'pending'
          ? 'loading'
          : reconciliation.state === 'error'
            ? 'error'
            : 'idle',
      );
    }
  }, [reconciliation]);

  useEffect(() => {
    let active = true;
    let timeoutId: ReturnType<typeof setTimeout>;
    const changeState = (state: LoadingState) => {
      if (active) callbacks.current.onChangeLoadingState(state);
    };
    const loadContext = () => callbacks.current.onLoadContext();

    const reconcile = () =>
      reconcileSession(session, `${statusId}:${refreshHeader!.id}`, () =>
        callbacks.current.onRefresh(),
      );

    const scheduleRefresh = (iteration: number) => {
      timeoutId = setTimeout(
        async () => {
          let polling = true;
          try {
            const handle = refreshHeader!.id;
            let request = session.requests.get(handle);
            if (!request) {
              request = client.asyncRefreshes.show(handle);
              session.requests.set(handle, request);
              const clearRequest = () => session.requests.delete(handle);
              void request.then(clearRequest, clearRequest);
            }
            const {
              async_refresh: { status, result_count },
            } = await request;
            if (!active) return;
            polling = false;

            if (status === 'finished') {
              void reconcile();
              return;
            } else if (iteration % LONG_RUNNING_FETCH_THRESHOLD === 0 && (result_count ?? 0) > 0) {
              await loadContext();
            }
            if (!active) return;
            scheduleRefresh(iteration + 1);
          } catch (error) {
            if (!active) return;
            if (polling && isUnavailableError(error)) {
              // Expired handles cannot be retried. Ask context for a fresh one instead.
              void reconcile();
            } else {
              changeState('error');
            }
          }
        },
        (refreshHeader!.retry ?? 1) * 1000,
      );
    };

    if (reconciliation && reconciliation.state !== 'success') return;

    if (refreshHeader && isEnabled && !reconciledHandles.has(`${statusId}:${refreshHeader.id}`)) {
      changeState('loading');
      scheduleRefresh(1);
    } else {
      changeState('idle');
    }

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [
    statusId,
    refreshHeader?.id,
    refreshHeader?.retry,
    isEnabled,
    client,
    retryKey,
    session,
    reconciliation,
  ]);
};

/**
 * Custom hook for detecting if the document is visible
 */
const useIsDocumentVisible = (onChange?: (isVisible: boolean) => void) => {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const newVisibility = !document.hidden;
      setIsVisible(newVisibility);
      onChange?.(newVisibility);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onChange]);

  return isVisible;
};

interface IRefreshController {
  statusId: string;
  statusCreatedAt: string;
  isLocal: boolean;
  asyncRefreshHeader?: AsyncRefreshHeader;
  onLoadContext: () => Promise<void>;
  onRefresh: () => Promise<void>;
}

/**
 * This component fetches new post replies in the background
 * and gives users the option to show them.
 *
 * The following three scenarios are handled:
 *
 * 1. When the browser tab is visible, replies are refetched periodically
 *    (more frequently for new posts, less frequently for old ones)
 * 2. Replies are refetched when the browser tab is refocused
 *    after it was hidden or minimised
 * 3. Supplied AsyncRefresh handles are polled until newly discovered replies
 *    have been imported and the selected post and context reconciled.
 */
const ActiveRefreshController: React.FC<IRefreshController & { session: RefreshSession }> = ({
  statusId,
  session,
  statusCreatedAt,
  asyncRefreshHeader,
  onLoadContext,
  onRefresh,
}) => {
  const intl = useIntl();
  const activeStatus = useRef<string | undefined>(statusId);
  useEffect(() => {
    activeStatus.current = statusId;
    setLoadingState('idle');
    return () => {
      activeStatus.current = undefined;
    };
  }, [statusId]);

  const [partialLoadingState, setLoadingState] = useState<LoadingState>(
    asyncRefreshHeader ? 'loading' : 'idle',
  );

  const [retryKey, setRetryKey] = useState(0);

  const hasPendingReplies = false;
  const loadingState = hasPendingReplies ? 'more-available' : partialLoadingState;

  // Prevent too-frequent context calls
  const debouncedFetchContext = useDebouncedCallback(
    () => {
      void onLoadContext().catch(() => {
        if (activeStatus.current === statusId) onChangeLoadingState('error');
      });
    },
    // Ensure the debounce is a bit shorter than the auto-fetch interval
    SHORT_AUTO_FETCH_REPLIES_INTERVAL - 500,
    {
      leading: true,
      trailing: false,
    },
  );

  const isDocumentVisible = useIsDocumentVisible((isVisible) => {
    // Auto-fetch new replies when the page is refocused
    if (isVisible && partialLoadingState !== 'loading') {
      void debouncedFetchContext();
    }
  });

  const onChangeLoadingState = (state: LoadingState) => {
    setLoadingState(state);
    switch (state) {
      case 'more-available':
        toast.info(messages.moreFound, {
          action: handleShowPending,
          actionLabel: messages.show,
        });
        break;
      case 'error':
        toast.info(messages.error, {
          action: () => session.retry?.(),
          actionLabel: messages.retry,
        });
        break;
      case 'success':
        toast.info(messages.success);
        break;
      default:
        break;
    }
  };

  // Poll any supplied handle, including handles for local posts.
  useCheckForReplies({
    statusId,
    session,
    refreshHeader: asyncRefreshHeader,
    isEnabled: isDocumentVisible,
    retryKey,
    onChangeLoadingState,
    onLoadContext,
    onRefresh,
  });

  // Only auto-fetch new replies if there's no ongoing remote replies check
  const shouldAutoFetchReplies = isDocumentVisible && partialLoadingState !== 'loading';

  const autoFetchInterval = useMemo(() => {
    return getIsThreadNew(statusCreatedAt)
      ? SHORT_AUTO_FETCH_REPLIES_INTERVAL
      : LONG_AUTO_FETCH_REPLIES_INTERVAL;
  }, [statusCreatedAt]);

  useEffect(() => {
    if (!shouldAutoFetchReplies) return;

    const intervalId = setInterval(() => {
      debouncedFetchContext();
    }, autoFetchInterval);

    return () => clearInterval(intervalId);
  }, [shouldAutoFetchReplies, autoFetchInterval, debouncedFetchContext]);

  useEffect(() => {
    // Hide success message after a short delay
    if (loadingState === 'success') {
      const timeoutId = setTimeout(() => {
        setLoadingState('idle');
      }, 2500);

      return () => clearTimeout(timeoutId);
    }
  }, [loadingState]);

  const handleShowPending = useCallback(() => {
    if (activeStatus.current !== statusId) return;
    const entry = session.reconciliation;
    if (entry?.state === 'pending') return;
    if (entry?.state === 'error') {
      void reconcileSession(session, entry.handle, onRefresh);
      return;
    }
    setLoadingState('loading');
    void onRefresh()
      .then(() => {
        if (activeStatus.current !== statusId) return;
        toast.success(messages.success);
        setLoadingState('success');
        setRetryKey((value) => value + 1);
      })
      .catch(() => {
        if (activeStatus.current === statusId) onChangeLoadingState('error');
      });
  }, [onRefresh, statusId, session]);

  // Toasts may outlive an owning view. Resolve Retry against the current owner,
  // and clear it when the last view leaves so old actions cannot touch another thread.
  useEffect(() => {
    const retry = () => {
      if (loadingState === 'error') handleShowPending();
    };
    session.retry = retry;
    return () => {
      if (session.retry === retry) session.retry = undefined;
    };
  }, [session, loadingState, handleShowPending]);

  if (loadingState === 'loading') {
    return (
      <div
        className='refresh-controller'
        role='status'
        aria-live='polite'
        aria-label={intl.formatMessage(messages.loadingInitial)}
      >
        <Spinner withText={false} />
      </div>
    );
  }

  return null;
};

const RefreshController: React.FC<IRefreshController> = (props) => {
  const { session, isOwner } = useRefreshSession(props.statusId);
  return isOwner ? <ActiveRefreshController key={session.id} {...props} session={session} /> : null;
};

export { RefreshController as default };
